import {
  Banner,
  BannerDescription,
  BannerTitle,
  Box,
  Grid,
  Heading,
  Badge,
  Icon,
  Image,
  Link,
  Panel,
  PanelInset,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import zapierWordmark from '@/assets/images/integrations/zapier-wordmark.svg';
import { IntegrationType, integrationIcon, integrationImageSrc } from '@/features/integrations';
import type { Integration } from '@/services/db/integration/entity';
import { CLICKED_ON, landingPageButtonClickedProperties } from '../analytics';
import { isEmptyString } from '@/utils/string';
import { isConnectPartner, typeLabel } from '../page-model';
import { CtaButton } from './cta-button';
import { UseCaseList } from './use-case-list';
import * as styles from './styles.css';
import { useAnalytics } from '@/services/analytics';

const PARTNERS_URL = 'https://customer.io/partners/';

type ExternalIntegrationPanelProps = {
  integration: Integration;
  workspaceId: string;
};

type IntegrationSectionProps = {
  integration: Integration;
};

/** The logo and name, linking to the partner's own site when the catalog names one. */
function IntegrationHeading({ integration }: IntegrationSectionProps) {
  const { track } = useAnalytics();
  const icon = integrationIcon(integration);
  // Catalog entries can carry an empty homepage as readily as none at all, and a
  // link to `""` reloads the page the reader is already on.
  const homepage = isEmptyString(integration.homepage) ? null : integration.homepage;

  return (
    <Link
      href={homepage ?? '#'}
      isExternal={homepage != null}
      isUnderlined={false}
      display="flex"
      alignItems="center"
      onClick={() => {
        void track(
          'integration',
          'integrationLandingPageButtonClicked',
          landingPageButtonClickedProperties(integration, CLICKED_ON.logoOrTitle),
        );
      }}
    >
      {icon == null ? (
        <Image className={styles.logo} src={integrationImageSrc(integration)} alt={`${integration.name} logo`} />
      ) : (
        <Icon mr="300" name={icon} color="subtle" />
      )}
      <Heading level="2" size="xl" m="0" color="active" className={styles.name}>
        {integration.name}
        {integration.type === IntegrationType.zapier ? (
          <>
            <Text size="sm" color="subtle" weight="semibold" ml="100">
              via
            </Text>
            <Image className={styles.zapierLogo} src={zapierWordmark} alt="Zapier" />
          </>
        ) : null}
      </Heading>
      {integration.beta ? <Badge ml="150">Beta</Badge> : null}
    </Link>
  );
}

/** TYPE and CATEGORY, the two facts the catalog always has. */
function IntegrationMeta({ integration }: IntegrationSectionProps) {
  const label = typeLabel(integration);

  return (
    <Stack mt="600" gap="200">
      <Stack gap="100" alignInline="start">
        <Text textTransform="uppercase" size="xxs" weight="semibold">
          Type
        </Text>
        {label == null ? null : <Badge color={label.color}>{label.text}</Badge>}
      </Stack>
      <Stack gap="100" alignInline="start">
        <Text textTransform="uppercase" size="xxs" weight="semibold">
          Category
        </Text>
        <Badge>{integration.category}</Badge>
      </Stack>
    </Stack>
  );
}

/**
 * A catalog integration's detail page: what it does, what you can do with it,
 * and the one button that starts the connection.
 */
export function ExternalIntegrationPanel({ integration, workspaceId }: ExternalIntegrationPanelProps) {
  return (
    <Panel>
      <PanelInset p="600">
        <IntegrationHeading integration={integration} />

        {isConnectPartner(integration) ? (
          <Box mt="200">
            <Banner variant="feature" mb="300">
              <BannerTitle>Connect partner</BannerTitle>
              <BannerDescription>
                We&rsquo;re proud to have {integration.name} as a{' '}
                <Link href={PARTNERS_URL} variant="secondary" isExternal>
                  Connect Partner
                </Link>{' '}
                that offers a first class integration with our platform.
              </BannerDescription>
            </Banner>
          </Box>
        ) : null}

        <Grid columns="9fr 3fr" mt="050">
          <Box className={styles.content} p="0" pr="300" py="600">
            <Text weight="bold" size="md" mb="150">
              {integration.description}
            </Text>
            <UseCaseList integration={integration} />
          </Box>
          <Box className={styles.meta} p="0" pl="300" py="600">
            <CtaButton integration={integration} workspaceId={workspaceId} />
            <IntegrationMeta integration={integration} />
          </Box>
        </Grid>
      </PanelInset>
    </Panel>
  );
}
